import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import type { Database } from '../types/database';

// Define UserRole based on the actual enum in supabase types
type UserRole = Database['public']['Enums']['user_role'];

interface ProtectedRouteProps {
  allowedRoles?: Database['public']['Enums']['user_role'][];
}

export default function ProtectedRoute({ allowedRoles }: ProtectedRouteProps) {
  const { user, profile, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || !profile) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if CupShup user is inactive - block access to the app
  if (profile.role === 'CupShup' && profile.status === 'inactive') {
    // Don't redirect if already on the inactive page to prevent infinite loop
    if (location.pathname !== '/inactive') {
      return <Navigate to="/inactive" replace />;
    }
  }

  // Check roles *after* confirming user and profile exist
  if (allowedRoles && profile.role && !allowedRoles.includes(profile.role as UserRole)) {
    // Redirect to appropriate dashboard based on actual role
    const roleRedirects = {
      CupShup: '/cupshup',
      Vendor: '/vendor',
      Client: '/client',
      Admin: '/cupshup'
    } as const; // Use as const

    // Ensure profile.role is a valid key before indexing
    const userRole = profile.role as UserRole;
    const redirectPath = userRole && (userRole in roleRedirects)
      ? roleRedirects[userRole]
      : '/login';
    return <Navigate to={redirectPath} replace />;
  }

  return <Outlet />;
}